import moment from 'moment/moment';
import { VALIDATION_CONFIG } from '../config/quizConfig';
import {
  getFocusAreaOptionsFromQuizData,
  getFocusPriorityRequiredCount,
  getFocusPriorityValue,
  getSelectedFocusAreas,
  normalizeFocusPriority,
} from './focusPriority';

const NAME_REGEX = /^\p{L}[\p{L}\p{M}]*(?:[\s'.\-\u2019]+\p{L}[\p{L}\p{M}]*)*$/u;

export const isValidName = (name) => NAME_REGEX.test(String(name || '').trim());

const getFieldValue = (fieldName, slideCode, slideValues) => {
  const stateValue = slideValues?.[slideCode]?.[fieldName]?.value;
  if (stateValue != null) return String(stateValue);

  if (typeof document === 'undefined') return '';

  return document.getElementById(fieldName)?.value || '';
};

// Validates name field
const validateName = (slideCode, slideValues) => {
  const name = getFieldValue('name', slideCode, slideValues).trim();
  
  if (!name) {
    return { valid: false, message: 'Name is required' };
  }
  
  if (!isValidName(name)) {
    return { 
      valid: false, 
      message: 'Name should only contain alphabetic characters.' 
    };
  }
  
  return { valid: true };
};

// Validates birthdate fields (day, month, year)
const validateBirthdate = () => {
  const dayElement = document.getElementById('day');
  const monthElement = document.getElementById('month');
  const yearElement = document.getElementById('year');
  
  const day = dayElement?.value?.trim() || '';
  const month = monthElement?.value?.trim() || '';
  const year = yearElement?.value?.trim() || '';
  
  if (!day || !month || !year) {
    return { valid: false, message: 'Birthdate is invalid.' };
  }
  
  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    return { valid: false, message: 'Birthdate is invalid.' };
  }
  
  const paddedDay = day.padStart(2, '0');
  const paddedMonth = month.padStart(2, '0');
  
  const date = moment(`${year}-${paddedMonth}-${paddedDay}`, 'YYYY-MM-DD', true);
  
  if (!date.isValid()) {
    return { valid: false, message: 'Birthdate is invalid.' };
  }
  
  return { valid: true }; 
};

// Validates weight and height fields
const validateWeightHeight = () => {
  const weightElement = document.getElementById('weight');
  const heightElement = document.getElementById('height');
  
  const weight = weightElement?.value?.trim() || '';
  const height = heightElement?.value?.trim() || '';
  
  if (!weight || isNaN(weight) || !height || isNaN(height)) {
    return { valid: false, message: 'Please enter valid weight and height' };
  }
  
  return { valid: true };
};

// Validates country selection from dropdown
const validateCountry = () => {
  const countryElement = document.getElementById('country');
  const selectedIndex = countryElement?.selectedIndex || 0;
  
  if (selectedIndex < 1) {
    return { valid: false, message: 'Please select a country' };
  }
  
  return { valid: true };
};

// Validates email format
const validateEmail = () => {
  const emailElement = document.getElementById('email');
  const email = emailElement?.value?.trim() || '';
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!email || !emailRegex.test(email)) {
    return { valid: false, message: 'Please enter valid Email.' };
  }
  
  return { valid: true };
};

// Validates that at least one option is selected in a checkbox group.
// Prefers slideValues (new card UI); falls back to legacy DOM (.checked on labels).
const validateSelectionRequired = (groupName, slideCode, slideValues) => {
  const slideData = slideValues?.[slideCode];
  if (slideData && typeof slideData === 'object') {
    for (const key of Object.keys(slideData)) {
      const v = slideData[key]?.value;
      if (v !== false && v !== null && v !== undefined && v !== '') {
        return { valid: true };
      }
    }
  }

  const selector = `#${groupName} label`;
  const labels = document.querySelectorAll(selector);
  for (let i = 0; i < labels.length; i++) {
    if (labels[i].classList.contains('checked')) {
      return { valid: true };
    }
  }

  return { valid: false, message: 'Please select an option.' };
};

const validateFocusPriority = (slideValues, quizData) => {
  const focusAreaOptions = getFocusAreaOptionsFromQuizData(quizData);
  const selectedFocusAreas = getSelectedFocusAreas(slideValues, focusAreaOptions);
  const requiredCount = getFocusPriorityRequiredCount(selectedFocusAreas);

  if (requiredCount === 0) return { valid: true };

  const priorityValue = normalizeFocusPriority(getFocusPriorityValue(slideValues), selectedFocusAreas);
  if (priorityValue.length >= requiredCount) return { valid: true };

  return { valid: false, message: 'Please rank your priorities.' };
};

// Map of validator functions by type
const VALIDATORS = {
  name: validateName,
  birthdate: validateBirthdate,
  'weight-height': validateWeightHeight,
  country: validateCountry,
  email: validateEmail,
  'selection-required': validateSelectionRequired,
  'focus-priority': validateFocusPriority,
};

// Main validation function - validates a slide based on its code
export const validateSlide = (slideCode, slideValues = {}, quizData = []) => {
  const validationConfig = VALIDATION_CONFIG[slideCode];

  if (!validationConfig) {
    return { valid: true };
  }

  const validator = VALIDATORS[validationConfig.type];

  if (!validator) {
    console.warn(`No validator found for type: ${validationConfig.type}`);
    return { valid: true };
  }

  if (validationConfig.type === 'selection-required') {
    return validator(validationConfig.groupName, slideCode, slideValues);
  }

  if (validationConfig.type === 'focus-priority') {
    return validator(slideValues, quizData);
  }

  return validator(slideCode, slideValues);
};
